package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.theme.AdminColors
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney

/**
 * Tono de una fila del desglose. Mismos colores que Reportes:
 * verde = cobrado/correcto, naranja = pendiente, ámbar = vuelto (atención), rojo = error, morado = dato informativo.
 */
enum class BreakdownTone(val color: Color) {
    OK(Color(0xFF2E7D32)),
    PENDING(Color(0xFFE65100)),
    ATTENTION(Color(0xFFF59E0B)),
    ERROR(Color(0xFFC62828)),
    INFO(Color(0xFF7C3AED)),
}

data class BreakdownRow(val text: String, val tone: BreakdownTone)

/**
 * Desglose de un cobro antes de confirmarlo: un renglón por método, monedero, descuento, vuelto,
 * pendientes en Bs y en $, y el total. Montos en centavos (Bs o $ según el campo).
 */
fun buildBulkPaymentBreakdown(
    lines: List<PaymentLineDraft>,
    walletApplyCents: Long,
    discountBsCents: Long,
    discountUsdCents: Long,
    changeBsCents: Long,
    changeUsdCents: Long,
    pendingBsCents: Long,
    pendingUsdCents: Long,
    dueBsCents: Long,
    /** Código del motivo del descuento (ver [BULK_DISCOUNT_REASONS]); se muestra junto al descuento si existe. */
    discountReason: String? = null,
): List<BreakdownRow> {
    val rows = mutableListOf<BreakdownRow>()
    val motivo = discountReason
        ?.let { code -> BULK_DISCOUNT_REASONS.firstOrNull { it.first == code }?.second }
        ?.let { " · $it" }
        .orEmpty()
    var paidBsCents = 0L
    var hasUsdLines = false
    for (line in lines) {
        val cents = parseBulkAmountToCents(line.amountInput)
        if (cents <= 0L) continue
        val usd = line.method.usesUsdAmount()
        if (usd) hasUsdLines = true else paidBsCents += cents
        val amount = formatMoney(cents.toInt(), if (usd) "USD" else GymlyCurrency.CODE)
        val ref = line.notes.trim().takeIf { it.isNotEmpty() }?.let { " · ref. $it" }.orEmpty()
        rows += BreakdownRow("${line.method.labelEs()}: $amount$ref", BreakdownTone.OK)
    }
    if (walletApplyCents > 0L) {
        rows += BreakdownRow(
            "Monedero: ${formatMoney(walletApplyCents.toInt(), GymlyCurrency.CODE)}",
            BreakdownTone.INFO,
        )
    }
    if (discountBsCents > 0L) {
        rows += BreakdownRow(
            "Descuento Bs: ${formatMoney(discountBsCents.toInt(), GymlyCurrency.CODE)}$motivo",
            BreakdownTone.INFO,
        )
    }
    if (discountUsdCents > 0L) {
        rows += BreakdownRow("Descuento $: ${formatMoney(discountUsdCents.toInt(), "USD")}$motivo", BreakdownTone.INFO)
    }
    if (changeBsCents > 0L) {
        rows += BreakdownRow(
            "Vuelto Bs: ${formatMoney(changeBsCents.toInt(), GymlyCurrency.CODE)}",
            BreakdownTone.ATTENTION,
        )
    }
    if (changeUsdCents > 0L) {
        rows += BreakdownRow("Vuelto $: ${formatMoney(changeUsdCents.toInt(), "USD")}", BreakdownTone.ATTENTION)
    }
    if (pendingBsCents > 0L) {
        rows += BreakdownRow(
            "Pendiente Bs: ${formatMoney(pendingBsCents.toInt(), GymlyCurrency.CODE)}",
            BreakdownTone.PENDING,
        )
    }
    if (pendingUsdCents > 0L) {
        rows += BreakdownRow("Pendiente $: ${formatMoney(pendingUsdCents.toInt(), "USD")}", BreakdownTone.PENDING)
    }
    // Total: los pagos en Bs menos el vuelto contra lo debido en Bs. Con $ en el mismo cobro no se compara el total.
    if (!hasUsdLines && paidBsCents > 0L && dueBsCents > 0L) {
        val netBsCents = paidBsCents - changeBsCents
        val excess = netBsCents - dueBsCents
        val due = formatMoney(dueBsCents.toInt(), GymlyCurrency.CODE)
        val net = formatMoney(netBsCents.toInt(), GymlyCurrency.CODE)
        rows += when {
            pendingBsCents > 0L || pendingUsdCents > 0L ->
                BreakdownRow("Cobrado $net de $due", BreakdownTone.PENDING)
            excess > 0L ->
                BreakdownRow(
                    "Excede lo debido en ${formatMoney(excess.toInt(), GymlyCurrency.CODE)} sin vuelto declarado",
                    BreakdownTone.ERROR,
                )
            else -> BreakdownRow("Cubre lo debido: $net de $due", BreakdownTone.OK)
        }
    }
    return rows
}

/** Lista del desglose: un punto de color por fila, con el mismo color que su tono. */
@Composable
fun BulkPaymentBreakdownList(rows: List<BreakdownRow>) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        rows.forEach { row ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    Modifier
                        .size(8.dp)
                        .clip(CircleShape)
                        .background(row.tone.color),
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    row.text,
                    color = row.tone.color,
                    fontSize = 12.sp,
                    fontWeight = if (row.tone == BreakdownTone.ERROR || row.tone == BreakdownTone.PENDING) {
                        FontWeight.SemiBold
                    } else {
                        FontWeight.Normal
                    },
                )
            }
        }
    }
}

/** Resumen de la tarjeta: el desglose si hay cobro configurado; si no, el texto de siempre. */
@Composable
fun BulkPaymentSummaryText(configured: Boolean, summary: String?, breakdown: List<BreakdownRow>) {
    if (configured && breakdown.isNotEmpty()) {
        BulkPaymentBreakdownList(breakdown)
    } else {
        Text(
            when {
                configured && !summary.isNullOrBlank() -> summary
                configured -> "Listo para registrar."
                else -> "Elige métodos de pago para este socio."
            },
            color = if (configured) AdminColors.Primary else AdminColors.TextMuted,
            fontSize = 12.sp,
        )
    }
}
